import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

// Open modals, oldest first — Esc closes only the top one when they stack
// (e.g. Move Job opened from inside Job Details).
const openStack = [];

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = '600px',
  showTitle = true
}) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    const token = {};
    openStack.push(token);
    function handleKey(e) {
      if (e.key === 'Escape' && openStack[openStack.length - 1] === token) {
        e.stopPropagation();
        onCloseRef.current?.();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      openStack.splice(openStack.indexOf(token), 1);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(20, 16, 12, 0.5)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        animation: 'twp-modal-fade 0.18s ease-out',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-card)',
          color: 'var(--text-primary)',
          borderRadius: '24px',
          maxWidth: maxWidth,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
          border: '1px solid var(--cell-border)',
          position: 'relative',
          animation: 'twp-modal-pop 0.22s cubic-bezier(0.2, 0.9, 0.3, 1.15)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — only renders if showTitle is true */}
        {showTitle && (
          <div style={{
            padding: '24px 32px',
            borderBottom: '1px solid var(--cell-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'var(--brand-brown)'
            }}>
              {title}
            </h2>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '24px',
                color: 'var(--text-tertiary)',
                cursor: 'pointer',
                padding: '4px 8px',
                lineHeight: 1
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Floating close button when title is hidden (so user can still close) */}
        {!showTitle && (
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: '14px',
              right: '14px',
              zIndex: 20,
              width: '34px',
              height: '34px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(20, 18, 15, 0.55)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              borderRadius: '50%',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.25)',
              color: '#fff',
              cursor: 'pointer'
            }}
          >
            <X size={17} strokeWidth={2.25} />
          </button>
        )}

        {/* Content — no extra padding when title is hidden, lets child fully control layout */}
        <div style={{ padding: showTitle ? '32px' : 0 }}>
          {children}
        </div>
      </div>
    </div>
  );
}
